import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import withRouter from 'HOCs/withRouter';
import Auth from '../../../../Auth';
import setLoginModalState from 'Actions/setLoginModalState';
import NewIconButton from 'Components/ThematicNewIconButton/ThematicNewIconButton';

import withTrack from 'HOCs/withTrack/withTrack';
import { CAR_COMPARE_FLOW_STEP, SOCIAL_TAP_LIKE, SOCIAL_TAP_UNLIKE } from 'Constants/tracking';
import { urls } from 'Reducers/items';
import { customActions as customUsersActions } from 'Reducers/users';
import { operateFavouriteItem } from 'Reducers/user';
import { USER_TYPE } from 'Constants/users';

import { post, del } from './../../../../server';
import withConfig from 'HOCs/withConfig/withConfig';
import { getUserTypeLowerCase } from 'Helpers/item';
import { favouriteItemsSelector } from 'Selectors/user';
import { getUserFavouritesIds } from 'Actions/user';
import { FAVOURITE, ICONS } from 'Constants/items';

export const FavoriteButton = ({
    trackerOrigins,
    track,
    getTrackingInfo,
    setLoginModalState,
    color,
    size,
    item,
    isFavorited,
    favoriteIt,
    removeFavorite,
    getUserFavouritesIds,
    userData
}) => {
    const [blockFavBtn, toggleBlockFavBtn] = useState(false);
    const itemId = item?.id;

    const toggleFavorite = useCallback(() => {
        const user = Auth.getUser();
        const dealerType = item?.user_type || getUserTypeLowerCase(userData) || USER_TYPE.REGULAR;

        if (blockFavBtn) {
            return;
        }

        if (user && itemId) {
            toggleBlockFavBtn(true);

            if (isFavorited) {
                removeFavorite(user.id, dealerType, (res, dispatch) => {
                    if (res.ok) {
                        dispatch(customUsersActions.operateCounter(user.id, 'favourites', 'SUBTRACT'));
                        dispatch(operateFavouriteItem(itemId, 'REMOVE'));
                        toggleBlockFavBtn(false);
                    }
                });
            }
            else {
                favoriteIt(user.id, dealerType, (res, dispatch) => {
                    if (res.ok) {
                        dispatch(customUsersActions.operateCounter(user.id, 'favourites', 'SUM'));
                        dispatch(operateFavouriteItem(res?.data?.data?.[0], 'ADD'));
                        toggleBlockFavBtn(false);
                    }
                });
            }
        }
    }, [blockFavBtn, favoriteIt, isFavorited, item.user_type, itemId, removeFavorite, userData]);

    const onClickFavorite = useCallback(ev => {
        if (ev) {
            ev.stopPropagation();
        }
        const loggedUser = Auth.getUser();
        const eventName = isFavorited ? SOCIAL_TAP_UNLIKE : SOCIAL_TAP_LIKE;

        if (loggedUser) {
            toggleFavorite();
            track(eventName, { ...getTrackingInfo(), select_from: CAR_COMPARE_FLOW_STEP.ADP });
        }
        else {
            setLoginModalState({
                state: true,
                redirectTo: '',
                trackProps: { origin: trackerOrigins.FAVOURITE },
                entryPoint: 2,
                callback: toggleFavorite
            });
        }
    }, [getTrackingInfo, isFavorited, setLoginModalState, toggleFavorite, track, trackerOrigins.FAVOURITE]);

    useEffect(() => {
        const user = Auth.getUser();

        if (user && user.id && itemId) {
            getUserFavouritesIds();
        }
    }, [itemId, getUserFavouritesIds]);

    const icon = isFavorited ? ICONS.FAV_ON : ICONS.FAV_OFF;

    return (
        <NewIconButton
            name={ FAVOURITE }
            icon={ icon }
            data-aut-id="btnFav"
            onClick={ onClickFavorite }
            animation={ false }
            color={ color }
            size={ size }
            className={ icon }
        />
    );
};

FavoriteButton.propTypes = {
    setLoginModalState: PropTypes.func.isRequired,
    track: PropTypes.func.isRequired,
    getTrackingInfo: PropTypes.func.isRequired,
    color: PropTypes.string,
    size: PropTypes.number,
    trackerOrigins: PropTypes.object.isRequired,
    item: PropTypes.object.isRequired,
    isFavorited: PropTypes.bool.isRequired,
    removeFavorite: PropTypes.func.isRequired,
    favoriteIt: PropTypes.func.isRequired,
    getUserFavouritesIds: PropTypes.func,
    config: PropTypes.shape({
        get: PropTypes.func
    }),
    userData: PropTypes.object
};

export const mapStateToProps = (state, { item }) => {
    const itemId = item?.id;

    return {
        isFavorited: favouriteItemsSelector(state, itemId),
        userData: state.users?.elements?.[item.user_id]
    };
};

export const mapDispatchToProps = (dispatch, { item, config }) => {
    const itemId = item?.id;
    const categoryId = item?.category_id;
    const params = {};
    const sitecode = config?.get('siteCode');
    const headers = { 'x-panamera-sitecode': sitecode };

    return {
        setLoginModalState: state => dispatch(setLoginModalState(state)),
        favoriteIt: (userId, dealerType, callback) => dispatch(post(urls.favoriteIt(userId), 'ITEMS', { ads: [{ adId: itemId, categoryId, dealerType }] }, headers)).then(res => callback(res, dispatch)),
        removeFavorite: (userId, dealerType, callback) => dispatch(del(urls.removeFavorite(userId, itemId, categoryId, dealerType), 'ITEMS', params, headers)).then(res => callback(res, dispatch)),
        getUserFavouritesIds: () => dispatch(getUserFavouritesIds())
    };
};

export default compose(
    withRouter,
    withTrack,
    withConfig,
    connect(mapStateToProps, mapDispatchToProps),
    React.memo
)(FavoriteButton);

